/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { STRIPE_KEY, STRIPE_PRICING_TABLE_ID } from '../../../config';

const RenderOrgBilling = (props) => {
  const { state, languageObj } = props;
  const [isCompareExpanded, setIsCompareExpanded] = useState(false);

  const copy = (languageObj && languageObj.copy) || {};
  const getCopy = (key, fallback) =>
    copy[key] !== undefined && copy[key] !== null ? copy[key] : fallback;
  const getArrayCopy = (key, fallback) =>
    Array.isArray(copy[key]) ? copy[key] : fallback;

  const handleToggleComparison = () => {
    setIsCompareExpanded((prev) => !prev);
  };
  
  const redirectToBilling = () => {
    const billingUrl = state.billing;
    window.open(billingUrl, "_blank").focus();
    
  };


  if (!state) return;

  // Function to capitalize the first letter of a string
  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const currentPlan = state.organizationObj.currentPlan ? state.organizationObj.currentPlan.toLowerCase() : '';

  if (state.loading) {
    return (
      <div className="OrgPayMe-emptyPage">
        <div className="OrgPayMe-center">
          <div className="OrgPayMe-btm-load"></div>
        </div>
      </div>
    );
  }

  const freePaymentMethods = {
    title: getCopy("freePaymentMethodsTitle", "Accept the following payment methods powered by Stripe"),
    listTitle: getCopy("freePaymentMethodsListTitle", "Major Card Networks"),
    items: getArrayCopy("freePaymentMethodsItems", ["Visa", "MasterCard", "American Express", "UnionPay"]),
  };

  const renderPriceLines = (key, defaultLines) => {
    const values = getArrayCopy(key, defaultLines);
    return (
      <>
        {values.map((line, index) => (
          <Fragment key={`${key}-${index}`}>
            {index === 0 ? line : (
              <span style={{ fontSize: "12px", fontWeight: "400" }}>{line}</span>
            )}
            {index < values.length - 1 ? <br /> : null}
          </Fragment>
        ))}
      </>
    );
  };

  const renderDescriptionLines = (key, defaultLines) => {
    const values = getArrayCopy(key, defaultLines);
    return (
      <>
        {values.map((line, index) => (
          <Fragment key={`${key}-${index}`}>
            {line}
            {index < values.length - 1 ? <br /> : null}
          </Fragment>
        ))}
      </>
    );
  };

  const PlanText = [
    {
      name: "free",
      price: renderPriceLines("freePriceLines", ["$0.00"]),
      description: renderDescriptionLines("freeDescriptionLines", [
        "Free for all",
        "charitable organizations",
        "registered in Hong Kong"
      ]),
      access: true,
      web: true,
      receipt: true,
      report: false,
      number: true,
      numberNote: getCopy("freeNumberNote", "Quick Payment only"),
      projectDetails: null,
      creditCard: true,
      applePay: false,
      googlePay: false,
      recurringTransactions: false,
      PayMe: false,
      multipleLanguages: false,
      verifiedNonprofit: true,
      coverFees: true,
      landingPageCustomization: false,
      reducedTransactionFee: false,
      platformFee: "2%",
      bgColor: "#93C7EA",
      txtColor: "#93C7EA",
    },
    {
      name: "plus",
      price: renderPriceLines("plusPriceLines", [
        "$459/month",
        "per month billed annually"
      ]),
      description: getCopy("plusDescription", "or $550/month billed monthly"),
      access: true,
      web: true,
      receipt: true,
      report: true,
      number: true,
      numberNote: getCopy("plusNumberNote", "Up to 3"),
      projectDetails: getCopy("plusProjectDetails", "Up to 3 payment items or donation projects"),
      creditCard: true,
      applePay: false,
      googlePay: false,
      recurringTransactions: false,
      PayMe: false,
      multipleLanguages: false,
      verifiedNonprofit: true,
      coverFees: true,
      landingPageCustomization: true,
      reducedTransactionFee: true,
      platformFee: "1%",
      bgColor: "#1390E3",
      txtColor: "#1390E3",
    },
    {
      name: "pro",
      price: renderPriceLines("proPriceLines", [
        "$792/month",
        "per month billed annually"
      ]),
      description: getCopy("proDescription", "or $950/month billed monthly"),
      access: true,
      web: true,
      receipt: true,
      report: true,
      number: true,
      numberNote: getCopy("proNumberNote", "Up to 5"),
      projectDetails: getCopy("proProjectDetails", "Up to 5 payment items or donation projects"),
      creditCard: true,
      applePay: true,
      googlePay: true,
      recurringTransactions: true,
      PayMe: true,
      multipleLanguages: true,
      verifiedNonprofit: true,
      coverFees: true,
      landingPageCustomization: true,
      reducedTransactionFee: true,
      platformFee: "1%",
      bgColor: "#0969AA",
      txtColor: "#0969AA",
    },
    {
      name: "premium",
      price: renderPriceLines("premiumPriceLines", [
        "$1,250/month",
        "per month billed annually"
      ]),
      description: getCopy("premiumDescription", "or $1,500/month billed monthly"),
      access: true,
      web: true,
      receipt: true,
      report: true,
      number: true,
      numberNote: getCopy("premiumNumberNote", "Unlimited"),
      projectDetails: getCopy("premiumProjectDetails", "Unlimited payment items and donation projects"),
      creditCard: true,
      applePay: true,
      googlePay: true,
      recurringTransactions: true,
      PayMe: true,
      multipleLanguages: true,
      customDomain: true,
      manualPayments: true,
      customInputFields: true,
      verifiedNonprofit: true,
      coverFees: true,
      landingPageCustomization: true,
      reducedTransactionFee: true,
      platformFee: "1% or 0.5%*",
      bgColor: "linear-gradient(45deg, rgba(162,248,255,1) 0%, rgba(0,88,149,1) 100%)",
      txtColor: "#52A9CB", 
    }
  ];

  const PLATFORM_FEE_DISCLAIMER = getCopy("platformFeeDisclaimer", "*If transaction volume exceeds over $400k in the past 12 months");

  const featureCellStyle = {
    display: "flex",
    height: "75px",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  };

  const featureLabelItemStyle = {
    height: "75px",
    justifyContent: "end",
    alignItems: "center",
    color: "black",
    textAlign: "left",
    fontSize: "10pt",
  };

  const renderCheckIcon = (enabled) => (
    enabled ? (
      <div className="EnPricing-confirmFeature" alt="Confirm" />
    ) : (
      <div className="EnPricing-crossFeature" alt="Cross" />
    )
  );

  /* eslint-disable react/display-name */
  const compareRows = [
    {
      key: "plans",
      label: getCopy("comparisonPlans", "Plans"),
      render: (plan) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            className="EnPricing-planDot"
            style={{
              background: plan.bgColor,
            }}
          />
          <div
            className="EnPricing-planTxt"
            style={{
              color: plan.txtColor,
              fontWeight: "600",
            }}
          >
            {capitalizeFirstLetter(plan.name)}
          </div>
        </div>
      ),
    },
    {
      key: "verified-nonprofit",
      label: getCopy("verifiedNonprofitLabel", "Included in the verified Nonprofit list on CheckPlus (Stay tuned for more exclusive offers!)"),
      render: (plan) => renderCheckIcon(plan.verifiedNonprofit),
    },
    {
      key: "org-dashboard",
      label: getCopy("organizationDashboard", "Access to Organization Dashboard"),
      render: (plan) => renderCheckIcon(plan.access),
    },
    {
      key: "items",
      label: getCopy("itemsProjects", "Number of Items/Projects/Projects"),
      render: (plan) =>
        plan.number ? (
          <div className="EnPricing-numberNote">{plan.numberNote}</div>
        ) : (
          renderCheckIcon(false)
        ),
    },
    {
      key: "automated-receipts",
      label: getCopy("automatedReceipts", "Automated Payment and Donation Receipt Sending"),
      render: (plan) => renderCheckIcon(plan.receipt),
    },
    {
      key: "cover-fees",
      label: getCopy("freeCoverFees", "Cover Fees: Donors can choose to Cover Transaction Fee for Nonprofits"),
      render: (plan) => renderCheckIcon(plan.coverFees),
    },
    {
      key: "landing-page",
      label: getCopy("createPaymentLandingPage", "Create a Payment Landing Page"),
      render: (plan) => renderCheckIcon(plan.web),
    },
    {
      key: "landing-customisation",
      label: getCopy("landingPageCustomization", "Payment Landing Page Customisation"),
      render: (plan) => renderCheckIcon(plan.landingPageCustomization),
    },
    {
      key: "system-report",
      label: getCopy("systemReportGeneration", "System Report Generation"),
      render: (plan) => renderCheckIcon(plan.report),
    },
    {
      key: "multiple-languages",
      label: getCopy("multipleLanguages", "Multiple Languages"),
      render: (plan) => renderCheckIcon(plan.multipleLanguages),
    },
    {
      key: "recurring",
      label: getCopy("monthlyRecurring", "Monthly Recurring Transactions"),
      render: (plan) => renderCheckIcon(plan.recurringTransactions),
    },
    {
      key: "manual-payments",
      label: getCopy("manualPaymentsExtended", "Manual Payments (e.g. Cheques, Octopus, Cash, etc.)"),
      render: (plan) => renderCheckIcon(plan.manualPayments),
    },
    {
      key: "custom-domains",
      label: getCopy("customDomainsAndEmail", "Custom Domains and Email Addresses"),
      render: (plan) => renderCheckIcon(plan.customDomain),
    },
    {
      key: "custom-inputs",
      label: getCopy("customInputFields", "Custom Input Fields"),
      render: (plan) => renderCheckIcon(plan.customInputFields),
    },
    {
      key: "payment-methods",
      label: getCopy("paymentMethodsHeader", "Payment Methods"),
      isSectionHeader: true,
      render: () => null,
    },
    {
      key: "credit-card",
      label: getCopy("creditCardMethods", "Credit Card (Via Stripe) Visa, MasterCard, AMEX, UnionPay"),
      render: (plan) => renderCheckIcon(plan.creditCard),
    },
    {
      key: "reduced-card-fee",
      label: getCopy("reducedCardFee", "Reduced Transaction Fee for Credit Card Transactions"),
      render: (plan) => renderCheckIcon(plan.reducedTransactionFee),
    },
    {
      key: "apple-pay",
      label: getCopy("applePay", "Apple Pay"),
      render: (plan) => renderCheckIcon(plan.applePay),
    },
    {
      key: "google-pay",
      label: getCopy("googlePay", "Google Pay"),
      render: (plan) => renderCheckIcon(plan.googlePay),
    },
    {
      key: "payme",
      label: getCopy("paymeLabel", "PayMe"),
      render: (plan) => renderCheckIcon(plan.PayMe),
    },
    {
      key: "platform-fee",
      label: getCopy("platformFeeLabel", "Platform Fee"),
      render: (plan) =>
        plan.platformFee ? (
          <div className="EnPricing-numberNote" style={{ textAlign: "center" }}>
            {plan.platformFee}
          </div>
        ) : (
          renderCheckIcon(false)
        ),
    },
  ];
  /* eslint-enable react/display-name */

  const renderPlanCell = (row, plan) =>
    row.isSectionHeader ? null : row.render(plan);

  const renderComparisonTable = (plans) => (
    <div className="EnPricing-featureContainer2">
      <div className="EnPricing-featureSet">
        {compareRows.map((row) => (
          <div
            style={{
              ...featureLabelItemStyle,
              fontWeight: row.isSectionHeader ? "600" : undefined,
            }}
            key={row.key}
          >
            <div
              className="EnPricing-featureText"
              style={row.isSectionHeader ? { textTransform: "uppercase" } : {}}
            >
              {row.label}
            </div>
          </div>
        ))}
      </div>

      {plans.map((plan, index) => (
        <div key={`${plan.name}-${index}`}>
          <div className="EnPricing-featureDescriptionRowBottom">
            {compareRows.map((row) => (
              <div style={featureCellStyle} key={row.key}>
                {renderPlanCell(row, plan)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="OrgBilling-mainContainer">
      <div className="OrgBilling-contentHeader">
        <div className="OrgBilling-info">{languageObj.pageTitle}</div>
      </div>
      
      <div className="OrgBilling-headingContainer">
        <div className="OrgBilling-heading">{getCopy("subscriptionHeading", "Subscription Plan")}</div>
        <div className="OrgBilling-headingContainer" >
          {getCopy("currentPlanLabel", "Current plan:")} {capitalizeFirstLetter(currentPlan)}
        </div>  

      </div>

      {currentPlan === "premium" || currentPlan === "pro" || currentPlan === "plus" ? (
        <div className="">
         
          <div className="OrgBilling-billingBtn" onClick={redirectToBilling}>
            <div className="OrgBilling-billingTxt">{getCopy("manageSubscription", "Manage your subscription")}</div>
          </div>  

          <div className="OrgBilling-pricingDetails">
            <div className="EnPricing-featureContainer">
              <div className="EnPricing-extraSpace"></div>


              {PlanText
                .map((plan, key) => {
                  return (
                    <div className="EnPricing-featureDescriptionColumn" key={key} style={{position: "relative", minHeight: "600px", paddingBottom: "200px", marginBottom: "70px", marginTop: "30px"}}>
                      <div
                        className="EnPricing-featureDescriptionRowTop"
                        style={{
                          background: plan.bgColor,
                        }}
                      >
                        <div className="EnPricing-planContainer">
                          <div className="EnPricing-planName">{capitalizeFirstLetter(plan.name)}</div>

                          <div className="EnPricing-planPrice">{plan.price}</div>

                          <div className="EnPricing-planDescription">
                            {plan.description}
                          </div>
                        </div>
                      </div>
                      <div className="EnPricing-featureContainerRowBottom">
                    
                        {currentPlan === plan.name && (currentPlan === "plus" || currentPlan === "pro" || currentPlan === "premium" ) ? (
                          <div className="EnPricing-getStarted" style={{
                            background: plan.bgColor,
                          }}>{getCopy("currentPlan", "Current Plan")}</div>
                        ) : null}

                        {currentPlan !== plan.name ? (
                          <div className="OrgBilling-getStarted">{getCopy("contactToSwitch", "Contact us to switch")}</div> ): null}

                        {plan.name === "plus" ? (
                          <div className="EnPricing-everythingPlus">
                            {getCopy("plusEverything", "Everything \"Free\" offers and plus the followings,")}
                          </div>
                        ) : (
                          null
                        )}

                        {plan.name === "pro" ? (
                          <div className="EnPricing-everythingPlus">
                            {getCopy("proEverything", "Everything \"Plus\" offers and plus the followings,")}
                          </div>
                        ) : (
                          null
                        )}


                        {plan.name === "premium" ? (
                          <div className="EnPricing-everythingPlus">
                            {getCopy("premiumEverything", "Everything Pro offers and plus the followings,")}
                          </div>
                        ) : (
                          null
                        )}

                        {plan.name === "free" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("freeCreateAccount", "Create an online account")}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.name === "free" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("freeCreateLanding", "Create a payment landing website")}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.name === "free" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("freeAcceptPayments", "Accept payments and donations")}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.name === "free" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("automatedReceipts", "Automated Payment and Donation Receipt Sending")}
                            </div>
                          </div>
                        ) : null}

                        {plan.name === "free" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {freePaymentMethods.title}
                              {freePaymentMethods.title ? <p /> : null}
                              {freePaymentMethods.listTitle ? (
                                <>
                                  <strong>{freePaymentMethods.listTitle}</strong>
                                  <br />
                                </>
                              ) : null}
                              {freePaymentMethods.items.map((item, index) => (
                                <Fragment key={index}>
                                  - {item}
                                  <br />
                                </Fragment>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.name === "free" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("freeCoverFees", "Cover Fees: Donors can choose to Cover Transaction Fee for Nonprofits")}
                            </div>
                          </div>
                        ) : null}

                        {plan.projectDetails ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {plan.projectDetails}
                            </div>
                          </div>
                        ) : null}



                        {plan.report && plan.name === "plus" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("generateReport", "Generate a system report")}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.name === "plus" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("landingPageCustomization", "Payment Landing Page Customisation")}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.name === "plus" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">
                              {getCopy("reducedPlatformFee", "Reduced Platform Fee")}
                            </div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.applePay && plan.name !== "premium" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("applePay", "Apple Pay")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.googlePay && plan.name !== "premium" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("googlePay", "Google Pay")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.recurringTransactions && plan.name !== "premium" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("monthlyRecurring", "Monthly Recurring Transactions")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.PayMe && plan.name !== "premium" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("paymeBusiness", "PayMe for Business")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.multipleLanguages && plan.name !== "premium" ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("multipleLanguages", "Multiple Languages")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.customDomain ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("customDomain", "Custom Domain")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.manualPayments ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("manualPayments", "Manual Payments (No platform and processing fee)")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        {plan.customInputFields ? (
                          <div className="EnPricing-paymeFeature">
                            <div
                              className="EnPricing-featureTick"
                              alt="Confirm"
                            />
                            <div className="EnPricing-featureTxt">{getCopy("customInputFields", "Custom Input Fields")}</div>
                          </div>
                        ) : (
                          <div />
                        )}

                        <div
                          style={{
                            background: "white",
                            border: `2px solid ${plan.txtColor}`,
                            borderRadius: "15px",
                            padding: "20px",
                            textAlign: "center",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                            width: "300px",
                            boxSizing: "border-box",
                            position: "absolute",
                            top: "750px",
                            left: "50%",
                            transform: "translateX(-50%)",
                          }}
                        >
                          <div style={{
                            fontSize: "14pt",
                            fontWeight: "600",
                            color: "#333",
                            marginBottom: "8px"
                          }}>
                            {getCopy("platformFeeLabel", "Platform Fee")}
                          </div>
                          <div style={{
                            fontSize: "18pt",
                            fontWeight: "700",
                            color: plan.txtColor
                          }}>
                            {plan.platformFee}
                          </div>

                          {plan.name === "premium" ? (
                            <div
                              style={{
                                marginTop: "12px",
                                fontSize: "10pt",
                                fontWeight: "400",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              {PLATFORM_FEE_DISCLAIMER}
                            </div>
                          ) : null}

                          <div style={{
                            marginTop: "15px",
                            paddingTop: "15px",
                            borderTop: "1px solid #e0e0e0"
                          }}>
                            <div style={{
                              fontSize: "12pt",
                              fontWeight: "600",
                              color: "#333",
                              marginBottom: "5px"
                            }}>
                              {getCopy("processingFeeWithColon", "Processing Fee:")}
                            </div>
                            <div style={{
                              fontSize: "11pt",
                              fontWeight: "400",
                              color: "#666",
                              fontStyle: "italic"
                            }}>
                              {getCopy("chargedByGateway", "Charged by Payment Gateway")}
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  );
                })}
              <div className="EnPricing-extraSpace"></div>
            </div>
            <div className="EnPricing-topColumnRight"></div>

            <button
              type="button"
              className="OrgBilling-compareButton"
              onClick={handleToggleComparison}
            >
              {isCompareExpanded ? getCopy("hideComparison", "Hide Comparison") : getCopy("comparePlans", "Compare All Plans")}
            </button>

            {isCompareExpanded && (
              <>
                {renderComparisonTable(PlanText)}

                <div
                  style={{
                    marginTop: "0px",
                    fontSize: "10pt",
                    color: "#666",
                    fontStyle: "italic",
                    marginLeft: "auto",
                    marginRight: "auto",
                    marginBottom: "50px",
                  }}
                >
                  {PLATFORM_FEE_DISCLAIMER}
                </div>
              </>
            )}

          </div>

        </div> ) :  <div className="OrgBilling-priceTableContainer">
        <stripe-pricing-table
          pricing-table-id={STRIPE_PRICING_TABLE_ID}
          publishable-key={STRIPE_KEY}
          customer-email={state.username}
        >
        </stripe-pricing-table>

        <button
          type="button"
          className="OrgBilling-compareButton"
          onClick={handleToggleComparison}
        >
          {isCompareExpanded ? getCopy("hideComparison", "Hide Comparison") : getCopy("comparePlans", "Compare All Plans")}
        </button>

        {isCompareExpanded && (
          <>
            {renderComparisonTable(PlanText)}
            <div
              style={{
                marginTop: "0px",
                fontSize: "10pt",
                color: "#666",
                fontStyle: "italic",
                marginLeft: "auto",
                marginRight: "auto",
                marginBottom: "50px",
              }}
            >
              {PLATFORM_FEE_DISCLAIMER}
            </div>
          </>
        )}

      </div>
      }

      <div className="OrgBilling-TransactionFeeContainer">
        <h1>{getCopy("processingFeeTitle", "Processing Fee")}</h1>
        {getCopy("chargedByGateway", "Charged by Payment Gateway")}
        <p />
      </div>

      <div className="OrgBilling-gridBGTransactionFee">
        <div className="OrgBilling-stripePaidContainer">
          <div className="OrgBilling-hsbc">
            <div className="OrgBilling-stripeLogo" />
            <div className="OrgBilling-bar" />
            <div className="OrgBilling-hsbcDescription">{getCopy("processingFeeLower", "Processing fee")}</div>
          </div>

          <div className="OrgBilling-hsbcText">
            {getCopy("stripeCharged", "Charged by Stripe")}
            <p />
            <strong>{getCopy("stripeDomesticVisa", "Domestic Visa/Mastercard")}</strong>
            <br />
            2.9% + HK$2.35

            <p />
            <strong>{getCopy("stripeDomesticAmex", "Domestic American Express")}</strong>
            <br />
            3.4% + HK$2.35
            <p />
            <strong>{getCopy("stripeInternationalCards", "International Cards")}</strong>
            <br />
            {getCopy("stripeInternationalNote", "+ 0.5% for international cards")}
          </div>
        </div>
        <div className="OrgBilling-hsbcContainer">
          <div className="OrgBilling-hsbc">
            <div className="OrgBilling-hsbcLogo" />
            <div className="OrgBilling-bar" />
            <div className="OrgBilling-hsbcDescription">{getCopy("processingFeeTitle", "Processing Fee")}</div>
          </div>

          <div className="OrgBilling-hsbcText">
            {getCopy("hsbcCharged", "Charged by HSBC HK")}
            <p />
            <strong>{getCopy("paymeBusiness", "PayMe for Business")}</strong>
            <br />
            1.5%
          
          </div>
        </div>
      </div>

     
    </div>
  );
};

RenderOrgBilling.propTypes = {
  state: PropTypes.object,
  languageObj: PropTypes.object,
  setOpenPrompt: PropTypes.func,
  this: PropTypes.any,
};


export default RenderOrgBilling;
